/**
 * Route → permission-key map for the Backoffice.
 *
 * This is a map of the routes we ship, NOT a catalog. The list of permissions
 * that exist lives in the database and reaches the app through the API; this
 * file only wires the keys we have routes for to their URLs, so guards, nav and
 * hub cards can reason about them. A key added by a migration but absent here
 * still appears in the permissions picker — it just does not drive navigation
 * until a route exists for it (sc-1215).
 */
export const BACKOFFICE_PERMISSION_KEYS = {
  users: 'backoffice.users',
  billing: 'backoffice.billing',
  analytics: 'backoffice.analytics',
  // The one escape hatch: see every department's rows without being a Director
  // or Admin. Everything else about WHAT you see follows from your own
  // department — see `analytics-department-tabs.ts`.
  analyticsAll: 'backoffice.analytics.all',
  devTesting: 'backoffice.dev_testing',
  hubspot: 'backoffice.integrations.hubspot',
  stripe: 'backoffice.integrations.stripe',
  mux: 'backoffice.integrations.mux',
  googleDrive: 'backoffice.integrations.google_drive',
  sharefileMigration: 'backoffice.integrations.sharefile_migration',
  rothRadarRulesEngine: 'backoffice.integrations.roth_radar.rules_engine',
  rothRadarProxy: 'backoffice.integrations.roth_radar.proxy',
  rothRadarAlertRuns: 'backoffice.integrations.roth_radar.alert_runs',
  rothRadarEngagement: 'backoffice.integrations.roth_radar.engagement',
  plaid: 'backoffice.integrations.plaid',
  front: 'backoffice.integrations.front',
  slack: 'backoffice.integrations.slack',
  meetingTranscripts: 'backoffice.integrations.meeting_transcripts',
  journeyScheduling: 'backoffice.journey_scheduling',
  terms: 'backoffice.terms',
} as const

/** Holding anything under this prefix means "some Backoffice access at all". */
export const BACKOFFICE_PREFIX = 'backoffice.'

/**
 * `backoffice.integrations.roth_radar` is a non-grantable interior node
 * (sc-1367) — its four children (`rules_engine`, `proxy`, `alert_runs`,
 * `engagement`) are the real grants. This prefix is what the hub page, the
 * Integrations card and the RothRadar™ page's own route guard use to ask
 * "does this caller hold ANY of the four".
 *
 * The trailing dot is load-bearing: `authHasAnyPermission`/`hasAnyWithPrefix`
 * do a bare `startsWith`, not the `.*` catalog convention, so without it this
 * would also match the parent key itself — which nobody holds, since it is
 * not grantable, but would still be a latent bug if that ever changed.
 */
export const ROTH_RADAR_PREFIX = 'backoffice.integrations.roth_radar.'

/**
 * Prefix for the Integrations hub. The hub has no key of its own: requiring a
 * separate parent grant would mean an admin could hand out a page while
 * forgetting the hub that links to it, leaving a user with access they cannot
 * navigate to.
 */
export const BACKOFFICE_INTEGRATIONS_PREFIX = 'backoffice.integrations.'
